// useReportMetrics.js - resolves the live METRIC_CATALOG values for one board-report run.
//
// Sourced from the SAME figure endpoints the report's chart blocks already fetch (KPIStripBlock,
// PerformanceTableBlock, ForecastBandBlock, RiskBridgeBlock, VarianceWaterfallBlock all call
// useBlockData - a react-query-backed cache keyed by `block:${method}:${endpoint}:...`). Calling
// the same endpoint URL here shares that cache: no extra network request, and the metric values
// stay in lockstep with whatever the blocks themselves render.
import useBlockData from "../blocks/useBlockData";
import { resolveMetrics } from "./reportMetrics";

// ENDPOINT_ENVELOPE - the fixed set of figure endpoints METRIC_CATALOG's entries reference (see
// reportMetrics.js's header comment), paired with each one's response envelope key (the same key
// the corresponding block already unwraps - KPIStripBlock reads `data.kpis`, PerformanceTableBlock
// `data.performance`, ForecastBandBlock `data.forecast_band`, RiskBridgeBlock `data.risk_bridge`,
// VarianceWaterfallBlock `data.pvm`). A module-level constant (not derived at call time) so the
// list below stays obviously in sync with the explicit useBlockData calls in the hook body.
const ENDPOINT_ENVELOPE = [
  ["figures/kpis", "kpis"],
  ["figures/performance", "performance"],
  ["figures/forecast-band", "forecast_band"],
  ["figures/risk-bridge", "risk_bridge"],
  ["figures/pvm", "pvm"],
];

/**
 * useReportMetrics - `useReportMetrics(boardReportId, runId) -> { metrics, isLoading }`.
 *
 * Calls useBlockData once per distinct figure endpoint, UNCONDITIONALLY and in a fixed order (the
 * Rules of Hooks require a stable call count/order across renders - looping useBlockData calls
 * over a runtime-varying list would violate that, so each endpoint gets its own named call here
 * even though ENDPOINT_ENVELOPE also lists them, for the unwrap/aggregate step below).
 *
 * `metrics` is resolveMetrics' pure output (never throws; a metric with no data yet resolves to
 * "—"). `isLoading` is true while ANY of the distinct endpoints is still loading, so a caller that
 * gates on it (e.g. the PDF poller, or an inspector/narrative block rendering a {{fig:KEY}} token)
 * waits for every metric to have a chance to resolve before it's read as final.
 */
export default function useReportMetrics(boardReportId, runId) {
  const base = `/api/v1/board-reports/${boardReportId}/runs/${runId}`;

  const kpis = useBlockData(`${base}/figures/kpis`);
  const performance = useBlockData(`${base}/figures/performance`);
  const forecastBand = useBlockData(`${base}/figures/forecast-band`);
  const riskBridge = useBlockData(`${base}/figures/risk-bridge`);
  const pvm = useBlockData(`${base}/figures/pvm`);

  const resultByEndpoint = {
    "figures/kpis": kpis,
    "figures/performance": performance,
    "figures/forecast-band": forecastBand,
    "figures/risk-bridge": riskBridge,
    "figures/pvm": pvm,
  };

  const figuresByEndpoint = {};
  let isLoading = false;
  ENDPOINT_ENVELOPE.forEach(([endpoint, envelopeKey]) => {
    const { data, isLoading: endpointLoading } = resultByEndpoint[endpoint];
    figuresByEndpoint[endpoint] = data ? data[envelopeKey] : undefined;
    if (endpointLoading) isLoading = true;
  });

  return { metrics: resolveMetrics(figuresByEndpoint), isLoading };
}
